<script setup>
import { dropdownTriggers } from '@/constants/dropdown';

const props = defineProps({
  opened: {
    type: Boolean,
    default: false,
  },

  position: {
    type: String,
    default: 'bottom-start',
  },
  triggerEvent: {
    type: String,
    default: dropdownTriggers.hover,
    validator(trigger) {
      return Object.values(dropdownTriggers).includes(trigger);
    },
  },
  referenceRef: {
    type: Object,
    default: undefined,
  },

  hoverHideDelay: {
    type: Number,
    default: 0,
  },
  gap: {
    type: Number,
    default: 0,
  },

  stretchContent: {
    type: Boolean,
    default: false,
  },
  useVIf: {
    type: Boolean,
    default: false,
  },

  disabled: {
    type: Boolean,
    default: false,
  },

  closeOnTriggerDoubleClick: {
    type: Boolean,
    default: true,
  },
});

const visible = ref(props.opened);

watch(
  () => props.opened,
  (opened) => {
    if (props.disabled) return;

    visible.value = opened;
  },
);

const rootRef = ref(null);

function open() {
  if (props.disabled || visible?.value) return;

  visible.value = true;
}
function close() {
  if (props.disabled || !visible?.value) return;

  visible.value = false;
}

const referenceElement = computed(() => props.referenceRef || rootRef.value);

// handling events
let hideTimeout = null;

// click
function handleTriggerClick() {
  if (props.triggerEvent === dropdownTriggers.click) {
    if (visible.value === false) {
      open();
    } else if (props.closeOnTriggerDoubleClick) {
      close();
    }
  }
}
onMounted(() => {
  if (props.triggerEvent === dropdownTriggers.click) {
    onClickOutside(rootRef, close);
  }
});

// hover
function handleTriggerPointerEnter() {
  if (props.triggerEvent === dropdownTriggers.hover) {
    clearTimeout(hideTimeout);
    open();
  }
}
function handleTriggerPointerLeave() {
  if (props.triggerEvent === dropdownTriggers.hover) {
    hideTimeout = setTimeout(close, props.hoverHideDelay);
  }
}

defineExpose({ open, close, visible });
</script>

<template>
  <div
    ref="rootRef"
    class="c-dropdown"
    :class="{ 'c-dropdown--stretched': stretchContent }"
    @pointerenter="handleTriggerPointerEnter"
    @pointerleave="handleTriggerPointerLeave"
  >
    <div class="c-dropdown__trigger" @click="handleTriggerClick">
      <slot name="trigger" :visible="visible" />
    </div>

    <Transition name="fade">
      <CFloating
        v-if="!useVIf || visible"
        v-show="useVIf || visible"
        class="c-dropdown__content"
        :reference-ref="referenceElement"
        :position="position"
        :gap="gap"
      >
        <slot name="content" :close="close" />
      </CFloating>
    </Transition>
  </div>
</template>

<style scoped lang="scss">
.c-dropdown {
  $parent: &;

  position: relative;

  @include hover {
    #{$parent} {
      &__content {
        z-index: 1001;
      }
    }
  }

  &__content {
    z-index: 1000;
  }

  &--stretched {
    #{$parent} {
      &__content {
        width: 100%;
      }
    }
  }
}
</style>
